import React, { useState, useEffect } from 'react';
import { VideoCaption } from '../VideoCaptionsManager';
import { cn } from "../../lib/utils";

interface VideoCaptionsOverlayProps {
  captions: VideoCaption[];
  currentTime: number;
  videoContainerRef: React.RefObject<HTMLDivElement | HTMLVideoElement>;
  videoDimensions?: { width: number; height: number } | null;
}

export default function VideoCaptionsOverlay({
  captions,
  currentTime,
  videoContainerRef,
  videoDimensions,
}: VideoCaptionsOverlayProps): JSX.Element | null {
  const [fontSizePx, setFontSizePx] = useState<number>(24);
  const [paddingPx, setPaddingPx] = useState<{ x: number; y: number }>({ x: 6, y: 3 });
  const [videoRect, setVideoRect] = useState<{ width: number; height: number; left: number; top: number } | null>(null);

  // Find the active caption
  const activeCaption = captions.find(
    (caption) => {
      const isActive = currentTime >= caption.startTime && currentTime <= caption.endTime;
      return isActive && caption.text.trim() !== '';
    }
  );

  // Get style properties
  const fontFamily = activeCaption?.fontFamily || 'Arial';
  const fontSize = activeCaption?.fontSize || 24;
  const color = activeCaption?.color || '#ffffff';
  const backgroundColor = activeCaption?.backgroundColor || 'rgba(0, 0, 0, 0.8)';
  const position = activeCaption?.position || 'bottom';

  // Calculate font size and get video position - overlay must match video exactly
  useEffect(() => {
    const updateSize = () => {
      if (!videoContainerRef.current || !videoDimensions) {
        setFontSizePx(fontSize);
        setPaddingPx({ x: fontSize * 0.25, y: fontSize * 0.15 });
        setVideoRect(null);
        return;
      }

      const container = videoContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      
      if (containerRect.width === 0 || containerRect.height === 0) {
        setVideoRect(null);
        return;
      }

      const videoAspect = videoDimensions.width / videoDimensions.height;
      const containerAspect = containerRect.width / containerRect.height;

      let displayWidth: number;
      let displayHeight: number;
      let offsetX = 0;
      let offsetY = 0;

      if (videoAspect > containerAspect) {
        displayWidth = containerRect.width;
        displayHeight = containerRect.width / videoAspect;
        offsetY = (containerRect.height - displayHeight) / 2;
      } else {
        displayHeight = containerRect.height;
        displayWidth = containerRect.height * videoAspect;
        offsetX = (containerRect.width - displayWidth) / 2;
      }

      setVideoRect({
        width: displayWidth,
        height: displayHeight,
        left: offsetX,
        top: offsetY,
      });

      const scale = displayHeight / videoDimensions.height;
      const scaledFontSize = fontSize * scale;
      
      setFontSizePx(scaledFontSize);
      setPaddingPx({ x: scaledFontSize * 0.25, y: scaledFontSize * 0.15 });
    };

    updateSize();

    const handleResize = () => updateSize();
    window.addEventListener('resize', handleResize);

    let containerObserver: ResizeObserver | null = null;
    if (videoContainerRef.current && typeof ResizeObserver !== 'undefined') {
      containerObserver = new ResizeObserver(updateSize);
      containerObserver.observe(videoContainerRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (containerObserver) {
        containerObserver.disconnect();
      }
    };
  }, [videoContainerRef, videoDimensions, fontSize, activeCaption]);

  if (!activeCaption) {
    return null;
  }

  const overlayStyle = videoRect ? {
    position: 'absolute' as const,
    left: `${videoRect.left}px`,
    top: `${videoRect.top}px`,
    width: `${videoRect.width}px`,
    height: `${videoRect.height}px`,
    bottom: 'auto',
  } : {
    position: 'absolute' as const,
    left: 0,
    right: 0,
    width: '100%',
  };

  let yPosition: number | undefined = undefined;
  if (videoRect && videoDimensions) {
    const effectiveVideoHeight = videoDimensions.height;
    const scale = videoRect.height / effectiveVideoHeight;
    
    let yPosInVideo: number;
    if (position === 'top') {
      yPosInVideo = effectiveVideoHeight * 0.1;
    } else if (position === 'center') {
      yPosInVideo = effectiveVideoHeight * 0.5;
    } else {
      yPosInVideo = effectiveVideoHeight * 0.9;
    }
    yPosition = yPosInVideo * scale;
  }

  return (
    <div
      style={{
        ...overlayStyle,
        display: 'flex',
        justifyContent: 'center',
        alignItems: position === 'center' ? 'center' : 'flex-start',
        pointerEvents: 'none',
        zIndex: 1001,
        ...(videoRect && yPosition !== undefined ? {
          paddingTop: position === 'center' ? undefined : `${yPosition}px`,
          transform: position === 'center' ? `translateY(${yPosition - videoRect.height / 2}px)` : undefined,
        } : {
          paddingTop: position === 'top' ? '10%' : undefined,
          paddingBottom: position === 'bottom' ? '10%' : undefined,
        }),
      }}
    >
      <div
        className="text-center font-medium leading-[1.4] rounded-md shadow-[0_2px_8px_rgba(0,0,0,0.9)] max-w-[90%]"
        style={{
          fontFamily: fontFamily,
          color: color,
          fontSize: `${fontSizePx}px`,
          paddingLeft: `${paddingPx.x}px`,
          paddingRight: `${paddingPx.x}px`,
          paddingTop: `${paddingPx.y}px`,
          paddingBottom: `${paddingPx.y}px`,
          backgroundColor: backgroundColor,
        }}
      >
        {activeCaption.text}
      </div>
    </div>
  );
}
